import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { BriefProgressEnum } from '../../../../common/general-types/briefs.dto';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { ManageBriefTabsService } from '../../service/manage-brief-tabs/manage-brief-tabs.service';
import { MbaIcon, MbaIconName } from '../assignment/atoms/mba-icon/mba-icon';
import {
  METRICS,
  PLATFORM_LABEL,
  PLATFORM_LOGO,
  ago,
  briefPlatforms,
  buildRows,
  chartSeries,
  costPerThousand,
  filterRows,
  formatMetric,
  lastSevenDays,
  metricLabel,
  money,
  notPostedRows,
  percent,
  compact,
  readPrefs,
  relativeDay,
  showEarlyNumbers,
  sortRows,
  toCsv,
  totals,
  utcDay,
  writePrefs,
} from './hybrid-reporting.helpers';
import { HybridReportingService, dataOf } from './hybrid-reporting.service';
import {
  HybridNotPosted,
  HybridPlatform,
  ReportMetric,
  ReportPrefs,
  ReportRange,
  ReportSort,
} from './hybrid-reporting.types';
import { HrChart } from './molecules/hr-chart/hr-chart';
import { HrPostDrawer } from './molecules/hr-post-drawer/hr-post-drawer';
import { HrPostsTable } from './molecules/hr-posts-table/hr-posts-table';

const REFRESH_POLL_MS = 15_000;
const REFRESH_MAX_POLLS = 40;

/**
 * Hybrid Reporting: every post on the brief with stats from Ultra Social (the source of truth)
 * and Super Social (fills only what Ultra Social lacks). Replaces the Reporting tab once signed
 * off; both live side by side until then.
 */
@Component({
  selector: 'mbt-hybrid-reporting',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [MbaIcon, HrChart, HrPostsTable, HrPostDrawer],
  providers: [HybridReportingService],
  templateUrl: './hybrid-reporting.html',
  styleUrl: './hybrid-reporting.css',
  host: { class: 'block h-full min-h-0' },
})
export class HybridReporting {
  private readonly data = inject(HybridReportingService);
  private readonly tabs = inject(ManageBriefTabsService);
  private readonly toast = inject(ToastNotificationService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly metricsDefs = METRICS;
  protected readonly logos = PLATFORM_LOGO;
  protected readonly platformLabel = PLATFORM_LABEL;
  protected readonly skeletonTiles = [1, 2, 3, 4, 5, 6, 7, 8];
  protected readonly skeletonRows = [1, 2, 3, 4, 5];

  private readonly userId = this.currentUser.shoutUser?.firebaseUid ?? null;
  protected readonly prefs = signal<ReportPrefs>(readPrefs(this.userId));
  protected readonly query = signal('');
  protected readonly openId = signal<string | null>(null);
  protected readonly refreshing = signal(false);
  protected readonly nudging = signal<string | null>(null);
  protected readonly nudgedUntil = signal<Record<string, string>>({});
  protected readonly now = signal(new Date());

  private readonly briefId = computed(() => this.tabs.focussedBrief()?.id ?? null);

  constructor() {
    effect(() => {
      const id = this.briefId();
      if (id) untracked(() => this.data.load(id));
    });
    effect(() => writePrefs(this.userId, this.prefs()));
    const tick = setInterval(() => this.now.set(new Date()), 60_000);
    this.destroyRef.onDestroy(() => clearInterval(tick));
  }

  // --- data ---------------------------------------------------------------------------------

  protected readonly structure = computed(() => dataOf(this.data.structure()));
  protected readonly metricsData = computed(() => dataOf(this.data.metrics()));
  protected readonly timeseriesData = computed(() => dataOf(this.data.timeseries()));

  protected readonly structureLoading = computed(() => !this.structure() && this.data.structure().status !== 'error');
  protected readonly structureFailed = computed(() => !this.structure() && this.data.structure().status === 'error');
  protected readonly statsLoading = computed(() => !this.metricsData() && this.data.metrics().status === 'loading');
  protected readonly statsFailed = computed(() => this.data.metrics().status === 'error');
  protected readonly seriesLoading = computed(() => !this.timeseriesData() && this.data.timeseries().status === 'loading');

  /** A notice when one of the sources didn't answer. */
  protected readonly sourceNotice = computed(() => {
    const s = this.metricsData()?.sources;
    if (!s) return null;
    const down = (v: string) => v === 'error' || v === 'unavailable';
    if (down(s.ultra) && down(s.super)) return 'Neither Ultra Social nor Super Social responded, so stats may be missing.';
    if (down(s.ultra)) return 'Ultra Social didn’t respond, so these stats come from Super Social only.';
    if (down(s.super)) return 'Super Social didn’t respond. Ultra Social stats are shown; gaps it would fill may be empty.';
    return null;
  });

  protected readonly currency = computed(() => this.structure()?.brief.currencyCode ?? 'gbp');
  protected readonly canSeeCost = computed(() => !!this.structure()?.viewer.canSeeCost);
  protected readonly paidMinor = computed(() => this.structure()?.cost?.paidForLivePosts ?? null);
  protected readonly visibleMetrics = computed(() => this.metricsDefs.filter((m) => m.key !== 'cpm' || this.canSeeCost()));

  protected readonly platforms = computed(() => briefPlatforms(this.structure()?.brief.socialChannels ?? []));
  protected readonly platform = computed<HybridPlatform | 'all'>(() => {
    const p = this.prefs().platform;
    return p !== 'all' && this.platforms().includes(p) ? p : 'all';
  });
  protected readonly activePlatform = computed<HybridPlatform | null>(() => (this.platform() === 'all' ? null : (this.platform() as HybridPlatform)));
  protected readonly metric = computed<ReportMetric>(() => {
    const m = this.prefs().metric;
    return m === 'cpm' && !this.canSeeCost() ? 'views' : m;
  });

  protected readonly allRows = computed(() => {
    const s = this.structure();
    return s ? buildRows(s, this.metricsData()) : [];
  });
  /** Platform filter applies to the summary, chart and table together. */
  protected readonly platformRows = computed(() => filterRows(this.allRows(), this.platform(), ''));
  protected readonly tableRows = computed(() => sortRows(filterRows(this.allRows(), this.platform(), this.query()), this.prefs().sort));
  protected readonly summaryTotals = computed(() => totals(this.platformRows()));
  protected readonly tableTotals = computed(() => totals(this.tableRows()));
  protected readonly briefEr = computed(() => (this.summaryTotals().views ? this.summaryTotals().er : null));
  protected readonly cpm = computed(() => costPerThousand(this.canSeeCost() ? this.paidMinor() : null, this.summaryTotals().views));

  protected readonly today = computed(() => utcDay(this.now()));
  protected readonly points = computed(() =>
    chartSeries(this.platformRows(), this.timeseriesData(), this.metric(), this.prefs().range, this.today(), this.paidMinor()),
  );

  protected readonly tiles = computed(() => {
    const t = this.summaryTotals();
    const ts = this.timeseriesData();
    const rows = this.platformRows();
    // A tile with no known value on any post reads "–", not 0 — the same rule as the table cells.
    const known = (key: ReportMetric): boolean => {
      if (key === 'cpm') return this.cpm() !== null;
      const field = key === 'er' ? 'er' : key;
      return rows.some((r) => r[field] !== null);
    };
    return this.visibleMetrics().map((m) => {
      const raw =
        m.key === 'er' ? t.er : m.key === 'cpm' ? this.cpm() : m.key === 'engagements' ? t.engagements : t[m.key];
      const value = known(m.key) ? raw : null;
      let sub: string | null;
      if (m.key === 'er') sub = value === null ? 'No stats yet' : `Across ${t.n} post${t.n === 1 ? '' : 's'}`;
      else if (m.key === 'cpm') sub = `${money((this.paidMinor() ?? 0) / 100, this.currency())} paid for live posts`;
      else if (value === null) sub = 'No stats yet';
      else sub = ts ? `+${compact(lastSevenDays(this.platformRows(), ts, m.key, this.today()))} in the last 7 days` : null;
      return { ...m, icon: m.icon as MbaIconName, value: formatMetric(m.key, value, this.currency()), sub };
    });
  });

  protected readonly notPostedAll = computed(() => this.structure()?.notPosted ?? []);
  protected readonly creatorNames = computed(() => {
    const out: Record<string, string> = {};
    for (const c of this.structure()?.creators ?? []) out[c.id] = c.name;
    return out;
  });
  protected readonly notPostedVisible = computed(() =>
    this.prefs().showNotPosted
      ? notPostedRows(this.notPostedAll(), this.platform(), this.query(), (id) => this.creatorNames()[id] ?? '')
      : [],
  );

  protected readonly progress = computed(() => {
    const live = this.allRows().length;
    const waiting = this.notPostedAll().length;
    const total = Math.max(live + waiting, 1);
    return { live, waiting, total: live + waiting, livePct: (live / total) * 100, waitingPct: (waiting / total) * 100 };
  });

  protected readonly byPlatform = computed(() => {
    const rows = this.allRows();
    const list = this.platforms().map((p) => {
      const t = totals(rows.filter((r) => r.post.platform === p));
      return { platform: p, ...t };
    });
    const max = Math.max(1, ...list.map((p) => p.views));
    return list.map((p) => ({ ...p, width: (p.views / max) * 100 }));
  });

  protected readonly lastRefreshed = computed(() => {
    const times = this.allRows()
      .map((r) => r.stats?.refreshedAt)
      .filter((t): t is string => !!t)
      .sort();
    return times[times.length - 1] ?? this.structure()?.refresh.lastRefreshedAt ?? null;
  });

  protected readonly refreshBlockedUntil = computed(() => {
    const next = this.structure()?.refresh.nextAllowedAt;
    return next && new Date(next).getTime() > this.now().getTime() ? next : null;
  });

  protected readonly refreshTooltip = computed(() => {
    const blocked = this.refreshBlockedUntil();
    const cadence = this.structure()?.viewer.refreshInterval === 'hourly' ? 'once an hour' : 'once a day';
    if (blocked) {
      const t = new Date(blocked);
      return `Stats can be refreshed ${cadence}. Next refresh ${relativeDay(blocked, this.now()).toLowerCase()} at ${t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return 'Pull the latest stats from every connected creator account';
  });

  protected readonly earlyNumbers = computed(() => showEarlyNumbers(this.allRows(), this.now()));
  protected readonly lastRefreshedText = computed(() => ago(this.lastRefreshed(), this.now()));
  protected readonly platformNames = computed(() => this.platforms().map((p) => PLATFORM_LABEL[p]).join(' and '));

  protected readonly openIndex = computed(() => this.tableRows().findIndex((r) => r.post.id === this.openId()));
  protected readonly openRow = computed(() => this.tableRows()[this.openIndex()] ?? null);
  protected readonly pinned = computed(() => {
    const r = this.openRow();
    return r?.postedAt ? { date: r.postedAt, creator: r.creator?.name ?? 'Creator' } : null;
  });

  protected readonly sortCaption = computed(() => {
    const sort = this.prefs().sort;
    return sort === 'posted' ? 'newest' : metricLabel(sort).toLowerCase();
  });

  protected readonly totalsLabel = computed(() => {
    const p = this.platform();
    return `${p === 'all' ? 'All' : PLATFORM_LABEL[p]} posts · ${this.tableRows().length}`;
  });

  protected readonly filtered = computed(() => this.platform() !== 'all' || !!this.query().trim());

  // --- interactions --------------------------------------------------------------------------

  protected setMetric(metric: ReportMetric): void {
    this.prefs.update((p) => ({ ...p, metric, sort: metric === 'cpm' ? p.sort : metric }));
  }

  protected setSort(sort: ReportSort): void {
    this.prefs.update((p) => ({ ...p, sort, metric: sort === 'posted' ? p.metric : sort }));
  }

  protected setRange(range: ReportRange): void {
    this.prefs.update((p) => ({ ...p, range }));
  }

  protected togglePlatform(platform: HybridPlatform | 'all'): void {
    this.prefs.update((p) => ({ ...p, platform: p.platform === platform ? 'all' : platform }));
  }

  protected toggleNotPosted(): void {
    this.prefs.update((p) => ({ ...p, showNotPosted: !p.showNotPosted }));
  }

  protected toggleCollapsed(): void {
    this.prefs.update((p) => ({ ...p, collapsed: !p.collapsed }));
  }

  protected clearFilters(): void {
    this.query.set('');
    this.prefs.update((p) => ({ ...p, platform: 'all' }));
  }

  protected onSearch(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  protected openPost(id: string): void {
    this.openId.set(this.openId() === id ? null : id);
  }

  protected step(dir: 1 | -1): void {
    const next = this.tableRows()[this.openIndex() + dir];
    if (!next) return;
    this.openId.set(next.post.id);
    queueMicrotask(() =>
      document.querySelector(`tr[data-post="${next.post.id}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }),
    );
  }

  protected retry(): void {
    const id = this.briefId();
    if (id) this.data.load(id, { force: true });
  }

  protected seeWhoIsPosting(): void {
    this.tabs.navigateToBriefTab(BriefProgressEnum.Assignment);
  }

  protected exportCsv(): void {
    const rows = this.tableRows();
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(this.structure()?.brief.title || 'report').replace(/[^\w\- ]+/g, '').trim() || 'report'} - posts.csv`;
    a.click();
    URL.revokeObjectURL(url);
    void this.toast.alert({ title: 'Export', message: `Exporting ${rows.length} posts as CSV`, type: 'success', icon: 'success' });
  }

  protected refresh(): void {
    const id = this.briefId();
    if (!id || this.refreshing() || this.refreshBlockedUntil()) return;
    this.refreshing.set(true);
    this.data.refresh(id).subscribe({
      next: (res) => this.pollRefresh(id, res.taskNames, 0, res.posts, res.creatorAccounts),
      error: (err: HttpErrorResponse) => {
        this.refreshing.set(false);
        const message = err.status === 429 ? (err.error?.message ?? 'Stats were refreshed recently.') : 'Couldn’t start a refresh. Try again shortly.';
        void this.toast.alert({ title: 'Refresh', message, type: err.status === 429 ? 'info' : 'error', icon: err.status === 429 ? 'info' : 'error' });
        this.data.reloadStats(id);
      },
    });
  }

  private pollRefresh(briefId: string, taskNames: string[], attempt: number, posts: number, accounts: number): void {
    const finish = () => {
      if (this.briefId() !== briefId) return;
      this.refreshing.set(false);
      this.data.reloadStats(briefId);
      void this.toast.alert({
        title: 'Stats refreshed',
        message: `Stats refreshed for ${posts} posts from ${accounts} creator account${accounts === 1 ? '' : 's'}`,
        type: 'success',
        icon: 'success',
      });
    };
    const timer = setTimeout(() => {
      if (this.briefId() !== briefId) return;
      if (!taskNames.length || attempt >= REFRESH_MAX_POLLS) return finish();
      this.data.refreshStatus(briefId, taskNames).subscribe({
        next: (s) => (s.status === 'complete' ? finish() : this.pollRefresh(briefId, taskNames, attempt + 1, posts, accounts)),
        error: () => finish(),
      });
    }, REFRESH_POLL_MS);
    this.destroyRef.onDestroy(() => clearTimeout(timer));
  }

  protected copyAdCode(code: string): void {
    const done = (ok: boolean) =>
      void this.toast.alert(
        ok
          ? { title: 'Copied', message: 'Ad code copied to clipboard', type: 'success', icon: 'success' }
          : { title: 'Copy failed', message: 'Could not copy the ad code', type: 'error', icon: 'error' },
      );
    const clipboard = navigator.clipboard;
    if (!clipboard?.writeText) {
      done(copyWithSelection(code));
      return;
    }
    // Some embedded or non-secure contexts reject the async API; fall back to a selection copy.
    clipboard.writeText(code).then(
      () => done(true),
      () => done(copyWithSelection(code)),
    );
  }

  protected nudge(np: HybridNotPosted): void {
    const id = this.briefId();
    if (!id || this.nudging()) return;
    this.nudging.set(np.applicationId);
    const name = this.creatorNames()[np.creatorId] ?? 'the creator';
    this.data.nudge(id, np.applicationId).subscribe({
      next: (res) => {
        this.nudging.set(null);
        this.nudgedUntil.update((m) => ({ ...m, [np.applicationId]: res.nextAvailableAt }));
        void this.toast.alert({ title: 'Nudge sent', message: `Reminder sent to ${name}`, type: 'success', icon: 'success' });
      },
      error: (err: HttpErrorResponse) => {
        this.nudging.set(null);
        if (err.status === 429 && err.error?.nextAvailableAt) {
          this.nudgedUntil.update((m) => ({ ...m, [np.applicationId]: err.error.nextAvailableAt }));
        }
        const message = err.error?.message ?? 'The reminder couldn’t be sent.';
        void this.toast.alert({ title: 'Nudge', message, type: err.status === 429 ? 'info' : 'error', icon: err.status === 429 ? 'info' : 'error' });
      },
    });
  }

  protected readonly percent = percent;
  protected readonly metricLabel = metricLabel;
  protected readonly formatMetric = formatMetric;
}

/** Legacy clipboard path for contexts where `navigator.clipboard.writeText` is unavailable or denied. */
function copyWithSelection(text: string): boolean {
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
  }
}
